import { Job, useFetchJobData } from "@/hooks/useJobQueries";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useApplication } from "./ApplicationContext";

export const CURRENT_JOB_ID_KEY = "currentJobId";

interface ActiveJobContextProps {
  job?: Job;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

const ActiveJobContext = createContext<ActiveJobContextProps | undefined>(
  undefined
);

interface ActiveJobProviderProps {
  children: ReactNode;
}

const ActiveJobProvider: React.FC<ActiveJobProviderProps> = ({ children }) => {
  const { application } = useApplication();
  // Get current job ID from local storage or URL
  const [currentJobId, setCurrentJobId] = useState<string | undefined>(() => {
    if (typeof window !== "undefined") {
      // First check if jobId is in URL params
      const urlParams = new URLSearchParams(window.location.search);
      const jobIdFromUrl = urlParams.get("jobId");

      // Return jobId from URL if present, otherwise check localStorage
      return (
        jobIdFromUrl ||
        localStorage.getItem(CURRENT_JOB_ID_KEY) ||
        application?.jobId
      );
    }
    return undefined;
  });

  // Store currentJobId to local storage whenever it changes
  useEffect(() => {
    if (currentJobId && typeof window !== "undefined") {
      localStorage.setItem(CURRENT_JOB_ID_KEY, currentJobId);
    } else if (typeof window !== "undefined" && !currentJobId) {
      localStorage.removeItem(CURRENT_JOB_ID_KEY);
    }
  }, [currentJobId]);

  // Update currentJobId when application changes
  useEffect(() => {
    if (application?.jobId) {
      setCurrentJobId(application.jobId);
    }
  }, [application]);

  const {
    data: job,
    isLoading,
    error,
    refetch,
  } = useFetchJobData(currentJobId);
  return (
    <ActiveJobContext.Provider
      value={{
        job,
        isLoading,
        error,
        refetch,
      }}
    >
      {children}
    </ActiveJobContext.Provider>
  );
};

const useActiveJob = (): ActiveJobContextProps => {
  const context = useContext(ActiveJobContext);
  if (!context) {
    throw new Error("useActiveJob must be used within an ActiveJobProvider");
  }
  return context;
};

export { ActiveJobProvider, useActiveJob };
